import { useEffect, useState, useCallback } from "react";
import { Facebook, Twitter, MessageCircle, Copy, Trophy } from "lucide-react";
import { toast } from "sonner";

import { api, formatApiError, getShareUrl } from "../lib/api";
import VoteForm from "../components/VoteForm";
import VoteConfirmation from "../components/VoteConfirmation";
import FloatingVoteNotifier from "../components/FloatingVoteNotifier";

const LOGO_URL =
  "https://www.ontour.scootive.pl/img/logo.svg";

const SOCIALS = [
  { id: "ig", label: "Instagram", url: "https://www.instagram.com/scootive" },
  { id: "fb", label: "Facebook", url: "https://www.facebook.com/scootive" },
  { id: "tt", label: "TikTok", url: "https://www.tiktok.com/@scootive" },
  { id: "yt", label: "YouTube", url: "https://www.youtube.com/@scootive" },
];

function diff(endsAt) {
  const end = new Date(endsAt).getTime();
  const now = Date.now();
  const ms = Math.max(0, end - now);
  return {
    ms,
    days: Math.floor(ms / 86_400_000),
    hours: Math.floor((ms % 86_400_000) / 3_600_000),
    minutes: Math.floor((ms % 3_600_000) / 60_000),
    seconds: Math.floor((ms % 60_000) / 1000),
  };
}

function Cell({ value, label }) {
  return (
    <div className="flex flex-col items-center">
      <span className="font-display text-4xl sm:text-5xl text-black tabular-nums leading-none">
        {String(value).padStart(2, "0")}
      </span>
      <span className="mt-1.5 text-[9px] font-medium uppercase tracking-[0.3em] text-black/45">
        {label}
      </span>
    </div>
  );
}

function Sep() {
  return (
    <span
      className="text-3xl sm:text-4xl text-brand-green leading-none"
      style={{ fontFamily: '"Bodoni Moda", "Times New Roman", serif', fontStyle: "italic" }}
    >
      /
    </span>
  );
}

function EditorialCountdown({ endsAt }) {
  const [t, setT] = useState(() => (endsAt ? diff(endsAt) : null));
  useEffect(() => {
    if (!endsAt) return;
    setT(diff(endsAt));
    const id = setInterval(() => setT(diff(endsAt)), 1000);
    return () => clearInterval(id);
  }, [endsAt]);
  if (!endsAt || !t) return null;
  if (t.ms === 0) {
    return (
      <p className="font-display text-2xl text-black/40 uppercase tracking-tight">
        Głosowanie zamknięte
      </p>
    );
  }
  return (
    <div data-testid="countdown" className="flex items-baseline gap-3 sm:gap-4">
      <Cell value={t.days} label="dni" />
      <Sep />
      <Cell value={t.hours} label="godz" />
      <Sep />
      <Cell value={t.minutes} label="min" />
      <Sep />
      <Cell value={t.seconds} label="sek" />
    </div>
  );
}

function MiniLeaderboard({ items, totalVotes, title, votedId }) {
  const top = (items || []).slice(0, 5);
  const max = top[0]?.votes || 1;
  if (top.length === 0) {
    return (
      <p className="text-sm text-black/40 font-serif italic">Brak głosów — bądź pierwszy.</p>
    );
  }
  return (
    <div className="w-full">
      <div className="flex items-end justify-between border-b border-black/10 pb-3 mb-5">
        <p className="text-[10px] uppercase tracking-[0.3em] text-black/55">{title}</p>
        <p
          className="text-[11px] tracking-wide text-black/40"
          style={{ fontFamily: '"Bodoni Moda", serif', fontStyle: "italic" }}
        >
          Top {top.length} · {totalVotes ?? 0} głosów
        </p>
      </div>
      <ol className="space-y-3">
        {top.map((r, i) => {
          const pct = Math.max(4, (r.votes / max) * 100);
          const isVoted = votedId === r.skatepark_id;
          return (
            <li
              key={r.skatepark_id}
              data-testid={`v2-leaderboard-row-${i}`}
              className="group cursor-default"
            >
              <div className="flex items-baseline justify-between mb-1.5">
                <div className="flex items-baseline gap-3 min-w-0">
                  <span
                    className="text-3xl text-black/15 tabular-nums leading-none w-9 shrink-0"
                    style={{ fontFamily: '"Bodoni Moda", serif', fontStyle: "italic" }}
                  >
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  {i === 0 && <Trophy className="w-3.5 h-3.5 brand-green shrink-0" />}
                  <span className="font-semibold text-sm tracking-wide uppercase truncate">
                    {r.city}
                  </span>
                  {isVoted && (
                    <span className="text-[8px] font-medium uppercase tracking-wider brand-green px-1.5 py-0.5 rounded-full bg-brand-green/10 shrink-0">
                      Twój głos
                    </span>
                  )}
                </div>
                <span className="text-xs tabular-nums text-black/55 shrink-0 ml-3">
                  {r.votes} · {r.percentage}%
                </span>
              </div>
              <div className="h-[2px] w-full bg-black/5 overflow-hidden">
                <div
                  className="h-full bg-brand-green transition-all duration-700"
                  style={{ width: `${pct}%` }}
                />
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

function NoCampaignEditorial() {
  return (
    <div data-testid="v2-no-campaign" className="w-full max-w-2xl">
      <p
        className="text-[10px] uppercase tracking-[0.35em] text-brand-green mb-5"
        style={{ fontFamily: '"Bodoni Moda", serif', fontStyle: "italic" }}
      >
        — Stay tuned —
      </p>
      <h1 className="font-display text-[58px] sm:text-[96px] uppercase leading-[0.88] tracking-tight">
        Wróć
        <br />
        <span className="text-brand-green">wkrótce.</span>
      </h1>
      <p className="mt-7 text-base text-black/55 max-w-md leading-relaxed">
        Aktualnie nie trwa żadne głosowanie. Trwają przygotowania do kolejnej trasy — śledź
        nasze profile, żeby nie przegapić startu.
      </p>
      <div className="mt-8 flex flex-wrap gap-4">
        {SOCIALS.map((s) => (
          <a
            key={s.id}
            href={s.url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`v2-social-${s.id}`}
            className="text-sm font-medium tracking-wide uppercase text-black/65 hover:text-brand-green border-b border-black/15 hover:border-brand-green pb-0.5 transition-colors"
          >
            {s.label} →
          </a>
        ))}
      </div>
    </div>
  );
}

export default function LandingPageV2() {
  const [data, setData] = useState({ campaign: null, leaderboard: [], total_votes: 0 });
  const [loaded, setLoaded] = useState(false);
  const [view, setView] = useState("vote"); // 'vote' | 'results'
  const [confirmation, setConfirmation] = useState(null);
  const [votedId, setVotedId] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const { data } = await api.get("/campaign/active");
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const t = setInterval(fetchData, 12000);
    return () => clearInterval(t);
  }, [fetchData]);

  const handleShare = async (platform) => {
    const url = getShareUrl();
    const text = "Głosuj na Scootive On Tour — zmobilizuj ziomków!";
    let shareUrl = "";
    if (platform === "fb") shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
    if (platform === "tw") shareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`;
    if (platform === "wa") shareUrl = `https://wa.me/?text=${encodeURIComponent(text + " " + url)}`;
    if (platform === "copy") {
      try {
        await navigator.clipboard.writeText(url);
        toast.success("Link skopiowany");
      } catch {
        toast.error("Nie udało się skopiować");
      }
      return;
    }
    window.open(shareUrl, "_blank", "noopener,noreferrer");
  };

  const campaign = data.campaign;
  const optionLabel = campaign?.option_label || "miejsce";
  const resultsTitle = campaign?.results_title || "Ranking";
  const displayMode = campaign?.display_mode === "radio" ? "radio" : "combobox";
  const currentYear = new Date().getFullYear();

  const handleVoted = (option, voteCount) => {
    setConfirmation({ option, votes: voteCount });
    setVotedId(option.id);
    fetchData();
  };

  const handleConfirmationDone = () => {
    setConfirmation(null);
    setView("results");
    fetchData();
  };

  return (
    <div
      data-testid="landing-v2"
      className="min-h-screen w-full text-black bg-[#F7F6F2] relative flex flex-col"
      style={{
        backgroundImage:
          "radial-gradient(60% 40% at 100% 0%, rgba(46,108,46,0.06) 0%, rgba(46,108,46,0) 60%)",
      }}
    >
      {/* Compact logo strip — replaces the previous masthead */}
      <div className="max-w-[1400px] mx-auto w-full px-5 sm:px-10 pt-7 sm:pt-10">
        <a
          href="https://www.scootive.pl"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="v2-logo-link"
          className="inline-block"
        >
          <img src={LOGO_URL} alt="Scootive" className="h-8 sm:h-9" />
        </a>
      </div>

      {/* Main grid */}
      <main className="flex-1 max-w-[1400px] mx-auto w-full px-5 sm:px-10 pt-10 sm:pt-14 pb-12">
        {!loaded ? (
          <p className="text-[10px] uppercase tracking-[0.3em] text-black/30">Ładowanie…</p>
        ) : !campaign ? (
          <NoCampaignEditorial />
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
            {/* LEFT — title + description + countdown as one organic block */}
            <section className="lg:col-span-7 relative order-1 lg:order-none">
              <h1
                data-testid="v2-hero-headline"
                className="font-display uppercase leading-[0.88] tracking-tight text-[44px] sm:text-[64px] lg:text-[84px] break-words"
              >
                {campaign.title}
              </h1>

              {campaign.subtitle && (
                <p
                  className="mt-5 text-xl sm:text-2xl text-black/70 max-w-2xl leading-snug"
                  style={{ fontFamily: '"Bodoni Moda", serif', fontStyle: "italic" }}
                >
                  {campaign.subtitle}
                </p>
              )}

              {campaign.description && (
                <p className="mt-4 text-[15px] text-black/55 max-w-xl leading-relaxed">
                  {campaign.description}
                </p>
              )}

              {/* Countdown — label on its own line, numerals below */}
              {view === "vote" && campaign.ends_at && (
                <div className="mt-8">
                  <p
                    className="text-[11px] uppercase tracking-[0.3em] text-black/45 mb-3"
                    style={{ fontFamily: '"Bodoni Moda", serif', fontStyle: "italic" }}
                  >
                    Do końca kampanii
                  </p>
                  <EditorialCountdown endsAt={campaign.ends_at} />
                </div>
              )}

              {/* Mini leaderboard — desktop only here; mobile renders after the vote card (see below) */}
              <div className="hidden lg:block mt-12 pt-8 border-t border-black/10">
                <MiniLeaderboard
                  items={data.leaderboard}
                  totalVotes={data.total_votes}
                  title={resultsTitle}
                  votedId={votedId}
                />
              </div>
            </section>

            {/* RIGHT — action panel (on desktop sticky on the right; on mobile comes right after the hero, before Top 5) */}
            <aside className="lg:col-span-5 lg:sticky lg:top-10 order-2 lg:order-none">
              <div className="bg-white border border-black/10 shadow-[0_30px_60px_-30px_rgba(0,0,0,0.18)] p-6 sm:p-8">
                <div className="flex items-center justify-between mb-6">
                  <span
                    className="text-[10px] uppercase tracking-[0.3em] text-black/45"
                    style={{ fontFamily: '"Bodoni Moda", serif', fontStyle: "italic" }}
                  >
                    {view === "vote" ? "Oddaj swój głos" : "Wyniki na żywo"}
                  </span>
                  <button
                    type="button"
                    data-testid="v2-view-toggle"
                    onClick={() => setView(view === "vote" ? "results" : "vote")}
                    className="text-[10px] uppercase tracking-[0.2em] text-black/55 hover:text-brand-green border-b border-black/15 hover:border-brand-green pb-0.5 transition-colors"
                  >
                    {view === "vote" ? "Wyniki →" : "← Wróć"}
                  </button>
                </div>

                {view === "vote" ? (
                  <>
                    <p className="text-[10px] font-medium uppercase tracking-[0.25em] text-black/40 mb-3">
                      Wybierz: <span className="text-brand-green">{optionLabel}</span>
                    </p>
                    <VoteForm
                      optionLabel={optionLabel}
                      hasPostalCodes={!!campaign?.has_postal_codes}
                      displayMode={displayMode}
                      onVoted={handleVoted}
                      onShowResults={() => setView("results")}
                      compact
                    />
                  </>
                ) : (
                  <MiniLeaderboard
                    items={data.leaderboard}
                    totalVotes={data.total_votes}
                    title={resultsTitle}
                    votedId={votedId}
                  />
                )}
              </div>

              <div className="mt-6 flex items-center justify-between gap-3 flex-wrap">
                <p className="text-[10px] uppercase tracking-[0.3em] text-black/45">
                  Zwołaj ekipę
                </p>
                <div className="flex items-center gap-1">
                  {[
                    { id: "fb", Icon: Facebook },
                    { id: "tw", Icon: Twitter },
                    { id: "wa", Icon: MessageCircle },
                    { id: "copy", Icon: Copy },
                  ].map(({ id, Icon }) => (
                    <button
                      key={id}
                      type="button"
                      data-testid={`v2-share-${id}`}
                      onClick={() => handleShare(id)}
                      className="w-9 h-9 inline-flex items-center justify-center border border-black/10 text-black/65 hover:text-brand-green hover:border-brand-green transition-colors"
                      aria-label={`Share ${id}`}
                    >
                      <Icon className="w-4 h-4" />
                    </button>
                  ))}
                </div>
              </div>
            </aside>

            {/* MOBILE-ONLY Top 5 — renders below the vote card on small screens */}
            <section
              data-testid="v2-mobile-leaderboard"
              className="lg:hidden order-3 pt-2"
            >
              <MiniLeaderboard
                items={data.leaderboard}
                totalVotes={data.total_votes}
                title={resultsTitle}
                votedId={votedId}
              />
            </section>
          </div>
        )}
      </main>

      {/* Slim footer — no border, just text */}
      <footer className="max-w-[1400px] mx-auto w-full px-5 sm:px-10 pb-6 pt-4">
        <p className="text-[11px] uppercase tracking-[0.3em] text-black/40">
          © {currentYear} Scootive
        </p>
      </footer>

      {/* Floating live notifier — bottom-right */}
      {campaign && <FloatingVoteNotifier />}

      {confirmation && (
        <VoteConfirmation
          option={confirmation.option}
          votes={confirmation.votes}
          onDone={handleConfirmationDone}
        />
      )}
    </div>
  );
}
